/**
 * `--tags` on the base writers, resolved against the ORG's one tag vocabulary.
 *
 * Bases store tag NAMES and the vocabulary is
 * closed at every WayAI-mediated write — the proxy refuses an unknown name.
 * Resolving here is not a second gate but a better message: the proxy can only say which
 * name it rejected, while this side can say what the org actually offers, without a second
 * command.
 *
 * The registry is read through the SAME client the write goes out on, so the vocabulary
 * checked is always the org the write attests to — a second org-selector ladder here is
 * what let `--org <not-a-uuid>` past the client's own guard, and what could validate a
 * name against an org the write was never going to reach.
 */

import { ApiError } from '../lib/api-client.js';
import { resolveTagNames } from '../lib/credential-utils.js';
import { expected } from '../lib/expected.js';
import { splitList } from './helpers.js';
import type { DataClient } from './client.js';

/**
 * Resolve a `--tags` value to the org tag names to store, or throw an expected error
 * naming the available tags.
 *
 * An empty value (`--tags ""`) is a CLEAR: it resolves to `[]` without reading the registry,
 * so removing every tag from a base never depends on the vocabulary being reachable.
 */
export async function resolveBaseTags(rawTags: string, client: DataClient): Promise<string[]> {
  const refs = splitList(rawTags);
  if (refs.length === 0) return [];

  let orgTags;
  try {
    orgTags = await client.listOrgTags();
  } catch (err) {
    // A caller the REGISTRY refuses may still be authorized for the WRITE, and the two are
    // gated differently: the tag registry asks for `org:read`, while the base upsert asks
    // for `org:write` and exempts platform admins altogether. A platform-operator account
    // with no org membership, and a `way_` token minted `org:write` alone, both land here —
    // so refusing would take away a write they hold on a read they do not need. The names
    // go up unresolved instead; the proxy's own gate is the authority either way, and it
    // reads OrgDO directly rather than through this endpoint.
    if (!(err instanceof ApiError) || (err.status !== 401 && err.status !== 403)) throw err;
    return refs;
  }

  try {
    return resolveTagNames(refs, orgTags);
  } catch (err) {
    // A wrong tag name is a user-actionable condition, so it prints and exits rather than
    // reaching Sentry as a crash (`lib/expected.ts`).
    throw expected(err instanceof Error ? err.message : String(err));
  }
}
